import { SHARED } from "./_shared.mjs";
import { attach, detach } from "./helpers.mjs";
const tldrawStateGlobalKey = Symbol.for("__@supersig/state__");
const tldrawStateGlobal = globalThis;
if (tldrawStateGlobal[tldrawStateGlobalKey]) {
    console.warn("Multiple instances of @supersig/state detected. Runtime state is shared via globalThis so behavior is correct, but you should still deduplicate for bundle size. Check your bundler for dual CJS/ESM resolution of @supersig/state.");
} else {
    tldrawStateGlobal[tldrawStateGlobalKey] = true;
}
export class CaptureStackFrame {
    below;
    child;
    offset = 0;
    numNewParents = 0;
    maybeRemoved;
    constructor(below, child){
        this.below = below;
        this.child = child;
    }
}
/**
 * Executes the given function without capturing any parents in the current capture context.
 *
 * This is mainly useful if you want to run an effect only when certain signals change while also
 * dereferencing other signals which should not cause the effect to rerun on their own.
 *
 * @example
 * ```ts
 * const name = atom('name', 'Sam')
 * const time = atom('time', () => new Date().getTime())
 *
 * setInterval(() => {
 *   time.set(new Date().getTime())
 * })
 *
 * react('log name changes', () => {
 * 	 print(name.get(), 'was changed at', unsafe__withoutCapture(() => time.get()))
 * })
 *
 * ```
 *
 * @public
 */ export function unsafe__withoutCapture(fn) {
    const oldStack = SHARED.captureStack;
    SHARED.captureStack = null;
    try {
        return fn();
    } finally{
        SHARED.captureStack = oldStack;
    }
}
export function startCapturingParents(child) {
    SHARED.captureStack = new CaptureStackFrame(SHARED.captureStack, child);
}
export function stopCapturingParents() {
    const frame = SHARED.captureStack;
    SHARED.captureStack = frame.below;
    const didParentsChange = frame.numNewParents > 0 || frame.offset !== frame.child.parents.length;
    if (!didParentsChange) {
        return;
    }
    for(let i = frame.offset; i < frame.child.parents.length; i++){
        const p = frame.child.parents[i];
        const parentWasRemoved = frame.child.parents.indexOf(p) >= frame.offset;
        if (parentWasRemoved) {
            detach(p, frame.child);
        }
    }
    frame.child.parents.length = frame.offset;
    frame.child.parentEpochs.length = frame.offset;
    const belowFrame = SHARED.captureStack;
    if (belowFrame?.maybeRemoved) {
        for(let i = 0; i < belowFrame.maybeRemoved.length; i++){
            const maybeRemovedParent = belowFrame.maybeRemoved[i];
            if (frame.child.parents.indexOf(maybeRemovedParent) === -1) {
                detach(maybeRemovedParent, frame.child);
            }
        }
    }
}
// this must be called after the parent is up to date
export function maybeCaptureParent(p) {
    const frame = SHARED.captureStack;
    if (frame) {
        const idx = frame.child.parents.indexOf(p);
        // if the child didn't deref this parent last time it executed, then idx will be -1
        // if the child did deref this parent last time but in a different order relative to other parents, then idx will be greater than frame.offset
        // if the child did deref this parent last time in the same order, then idx will be the same as frame.offset
        // if the child did deref this parent already during this capture session then 0 <= idx < frame.offset
        if (idx < 0) {
            frame.numNewParents++;
            if (frame.child.isActivelyListening) {
                attach(p, frame.child);
            }
        }
        if (idx < 0 || idx >= frame.offset) {
            if (idx !== frame.offset && idx > 0) {
                const maybeRemovedParent = frame.child.parents[frame.offset];
                if (!frame.maybeRemoved) {
                    frame.maybeRemoved = [
                        maybeRemovedParent
                    ];
                } else if (frame.maybeRemoved.indexOf(maybeRemovedParent) === -1) {
                    frame.maybeRemoved.push(maybeRemovedParent);
                }
            }
            frame.child.parents[frame.offset] = p;
            frame.child.parentEpochs[frame.offset] = p.lastChangedEpoch;
            frame.offset++;
        }
    }
}
/**
 * A debugging tool that tells you why a computed signal or effect is running.
 * Call in the body of a computed signal or effect function.
 *
 * @example
 * ```ts
 * const name = atom('name', 'Bob')
 * react('greeting', () => {
 * 	whyAmIRunning()
 *	print('Hello', name.get())
 * })
 *
 * name.set('Alice')
 *
 * // 'greeting' is running because:
 * //     'name' changed => 'Alice'
 * ```
 *
 * @public
 */ export function whyAmIRunning() {
    const child = SHARED.captureStack?.child;
    if (!child) {
        throw new Error("whyAmIRunning() called outside of a reactive context");
    }
    const changedParents = [];
    for(let i = 0; i < child.parents.length; i++){
        const parent = child.parents[i];
        if (parent.lastChangedEpoch > child.parentEpochs[i]) {
            changedParents.push(parent);
        }
    }
    if (changedParents.length === 0) {
        // eslint-disable-next-line no-console
        console.log(child.name, "is running but none of the parents changed");
    } else {
        // eslint-disable-next-line no-console
        console.log(child.name, "is running because:");
        for (const changedParent of changedParents){
            // eslint-disable-next-line no-console
            console.log("\t", changedParent.name, "changed =>", changedParent.__unsafe__getWithoutCapture());
        }
    }
}
